if (!customElements.get('gsap-featured-product')) {
  customElements.define('gsap-featured-product', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.mediaObserver?.disconnect();
      this.motionContext?.revert();
      this.motionMatchMedia?.revert();

      this.abortController = new AbortController();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.galleryMediaQuery = matchMedia('(max-width: 989px)');
      this.galleryMediaQuery.addEventListener('change', () => {
        this.mediaObserver?.disconnect();
        this.observeMedia();
      }, { signal: this.abortController.signal });
      this.mediaList = this.querySelector('[data-gfp-media-list]');
      this.mediaList?.setAttribute('data-lenis-prevent', '');
      this.mediaItems = [...this.querySelectorAll('[data-gfp-media-frame][data-media-id]')];
      this.thumbnails = [...this.querySelectorAll('[data-gfp-thumbnail]')];
      this.pagination = this.querySelector('[data-gfp-pagination]');
      // Each desktop media frame has its own cue; the final one points to related products.
      this.querySelectorAll('.gsap-featured-product__scroll-cue').forEach(cue => cue.remove());
      this.galleryCues = new Map();
      this.mediaItems.forEach(frame => {
        const cue = document.createElement('button');
        cue.type = 'button';
        cue.className = 'gsap-featured-product__scroll-cue';
        cue.hidden = true;
        cue.setAttribute('aria-label', 'Scroll to next photo');
        cue.addEventListener('click', () => {
          if (this.galleryMediaQuery.matches) return;
          const visible = this.mediaItems.filter(item => !item.hidden);
          const position = visible.indexOf(frame);
          if (position < 0) return;
          const next = visible[position + 1];
          if (next) {
            this.selectMedia(next.dataset.mediaId, true);
            return;
          }
          const related = this.querySelector('[data-gfp-related]');
          if (related) {
            window.scrollTo({
              top: Math.max(0, window.scrollY + related.getBoundingClientRect().top - 100),
              behavior: this.reducedMotion ? 'auto' : 'smooth'
            });
          }
        }, { signal: this.abortController.signal });
        frame.appendChild(cue);
        this.galleryCues.set(frame, cue);
      });
      const warmVariantImages = () => {
        if (!this.isConnected) return;
        const images = this.mediaItems.map(frame => frame.querySelector('img.gsap-featured-product__image')).filter(image => image && /(?:[-_ ]01(?:[_ .]|$)|pair)/i.test(image.src));
        images.forEach(image => { image.loading = 'eager'; image.fetchPriority = 'low'; });
      };
      if (document.readyState === 'complete') warmVariantImages();
      else window.addEventListener('load', warmVariantImages, { once:true, signal:this.abortController.signal });

      try {
        this.states = JSON.parse(this.querySelector('[data-product-states]')?.textContent || '[]');
      } catch {
        this.states = [];
      }

      this.thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', () => this.selectMedia(thumbnail.dataset.mediaId, true), {
          signal: this.abortController.signal
        });
      });

      // Pair/Single controls can update selection without dispatching variant:change.
      const picker = this.querySelector('[data-product-variant-picker]');
      const returnOnSelection = event => {
        const control = event.target.closest('label, button, input, [role="radio"]');
        if (!control || !picker?.contains(control)) return;
        if (control.closest('[data-option-position]')?.dataset.optionPosition !== '2') return;
        requestAnimationFrame(() => this.returnToGalleryStart());
      };
      picker?.addEventListener('click', returnOnSelection, { signal: this.abortController.signal });
      picker?.addEventListener('change', returnOnSelection, { signal: this.abortController.signal });

      this.querySelectorAll('.gsap-featured-product__series-link').forEach(link => {
        link.addEventListener('mouseenter', () => {
          const preload = document.createElement('link');
          preload.rel = 'prefetch';
          preload.href = link.href;
          document.head.appendChild(preload);
        }, { once: true, signal: this.abortController.signal });
      });
      // Product zoom remains active without the inter-page loading curtain is covered from <head>, before any first paint.
      const relatedGallery = this.querySelector('.gsap-featured-product__related-gallery');
      const informationPanel = this.querySelector('.gsap-featured-product__content-inner');
      if (relatedGallery && informationPanel) {
        if (!this.relatedPlaceholder?.parentNode) {
          this.relatedPlaceholder = document.createComment('');
          relatedGallery.before(this.relatedPlaceholder);
        }
        const mobileLayout = matchMedia('(max-width: 989px)');
        const placeRelated = () => {
          if (mobileLayout.matches) informationPanel.appendChild(relatedGallery);
          else this.relatedPlaceholder.after(relatedGallery);
        };
        placeRelated();
        mobileLayout.addEventListener('change', placeRelated, { signal: this.abortController.signal });
      }
      this.relatedTrack = this.querySelector('[data-gfp-related-track]');
      const relatedScroll = direction => {
        if (!this.relatedTrack) return;
        const distance = Math.max(150, this.relatedTrack.clientWidth * .72);
        this.relatedTrack.scrollBy({
          left: distance * direction,
          behavior: this.reducedMotion ? 'auto' : 'smooth'
        });
      };
      this.querySelector('[data-gfp-related-prev]')?.addEventListener('click', () => relatedScroll(-1), {
        signal: this.abortController.signal
      });
      this.querySelector('[data-gfp-related-next]')?.addEventListener('click', () => relatedScroll(1), {
        signal: this.abortController.signal
      });

      this.addEventListener('variant:change', event => {
        clearTimeout(this.entranceTimer);
        clearTimeout(this.entranceFallback);
        this.classList.remove('gfp-entrance-unplayed');
        const state = this.states.find(item => String(item.id) === String(event.detail?.variant?.id));
        this.currentSelection = state?.selectionKey || event.detail?.variant?.option2;
        this.filterFinish(state?.finishKey || event.detail?.variant?.option1);
        this.renderPairPreview(this.currentSelection);
        this.syncGalleryCues();
        this.returnToGalleryStart();
        this.updatePairReference(state);

        const supportCopy = this.querySelector('[data-gfp-support-copy]');
        if (supportCopy && state?.supportText) {
          supportCopy.textContent = state.supportText;
        }
      }, { signal: this.abortController.signal });

      // Keep each finish's numbered photographs in a predictable 01 → 06 order.
      const mediaOrder = item => {
        const image = item.querySelector('img.gsap-featured-product__image');
        const source = image?.currentSrc || image?.src || '';
        const alt = image?.alt || '';
        if (item.dataset.finish === 'pair') return 0;
        const match = (source + ' ' + alt).match(/(?:gold|silver|copper)[-_ ](\d{2})(?:[_\. ]|$)/i);
        return match ? Number(match[1]) : 99;
      };
      const sortedMedia = [...this.mediaItems].sort((a, b) => mediaOrder(a) - mediaOrder(b));
      sortedMedia.forEach(item => this.mediaList?.appendChild(item));
      this.mediaItems = sortedMedia;
      const selectedId = new URLSearchParams(window.location.search).get('variant');
      const initialState = this.states.find(state => String(state.id) === String(selectedId)) || this.states[0];
      this.currentSelection = initialState?.selectionKey || initialState?.options?.[1];
      this.filterFinish(initialState?.finishKey || initialState?.options?.[0]);
      this.renderPairPreview(this.currentSelection);
      this.updatePairReference(initialState);
      this.syncGalleryCues();
      this.startInitialBraceletReveal();
      this.observeMedia();
      requestAnimationFrame(() => this.initMotion());
    }

    updatePairReference(state) {
      const compare = this.querySelector('[data-gfp-pair-reference]');
      const compareValue = this.querySelector('[data-gfp-pair-reference-value]');
      if (!compare || !compareValue || !state) return;

      const single = this.states.find(variant => variant.finishKey === state.finishKey && variant.selectionKey === 'single');
      const referenceAmount = Number(single?.priceAmount || 0) * 2;
      const show = state.selectionKey === 'pair' && referenceAmount > Number(state.priceAmount || 0);
      compare.hidden = !show;
      if (!show) return;

      compareValue.textContent = this.formatMoneyWithDecimals(referenceAmount);
    }

    formatMoneyWithDecimals(amount) {
      const moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      const token = moneyFormat.match(/{{\s*(\w+)\s*}}/)?.[1] || 'amount_with_comma_separator';
      const commaDecimal = token.includes('comma_separator');
      const spaceThousands = token.includes('space_separator');
      const apostropheThousands = token.includes('apostrophe_separator');
      const decimalMark = commaDecimal ? ',' : '.';
      const thousandsMark = spaceThousands ? ' ' : apostropheThousands ? "'" : commaDecimal ? '.' : ',';
      const [integer, fraction] = (amount / 100).toFixed(2).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsMark);
      const formattedMoney = moneyFormat.replace(/{{\s*\w+\s*}}/, `${grouped}${decimalMark}${fraction}`);
      const template = document.createElement('template');
      template.innerHTML = formattedMoney;
      return template.content.textContent || formattedMoney;
    }

    startInitialBraceletReveal() {
      if (this.reducedMotion) {
        this.classList.remove('gfp-entrance-unplayed');
        return;
      }
      const frame = this.mediaItems.find(item => !item.hidden && item.querySelector('img.gsap-featured-product__image'));
      if (!frame) {
        this.classList.remove('gfp-entrance-unplayed');
        return;
      }
      // In Single/Pair mode the visible bracelet is a JS-created preview, not the underlying photo.
      const preview = frame.querySelector('[data-gfp-pair-preview]');
      const artwork = preview || frame.querySelector('img.gsap-featured-product__image');
      const image = preview?.querySelector('img') || artwork;
      if (!artwork) {
        this.classList.remove('gfp-entrance-unplayed');
        return;
      }
      frame.classList.add('gfp-entrance-target');
      const started = performance.now();
      let revealed = false;
      const reveal = () => {
        if (revealed || !this.isConnected) return;
        revealed = true;
        const wait = Math.max(0, 1000 - (performance.now() - started));
        this.entranceTimer = window.setTimeout(() => {
          if (!this.isConnected) return;
          requestAnimationFrame(() => requestAnimationFrame(() => {
            frame.classList.add('gfp-entrance-playing');
            this.entranceTimer = window.setTimeout(() => {
              frame.classList.remove('gfp-entrance-playing', 'gfp-entrance-target');
              this.classList.remove('gfp-entrance-unplayed');
            }, 3300);
          }));
        }, wait);
      };
      // Never hang indefinitely when the selected image is cached, delayed or fails.
      if (!image || image.complete) reveal();
      else {
        image.addEventListener('load', reveal, { once: true, signal: this.abortController.signal });
        image.addEventListener('error', reveal, { once: true, signal: this.abortController.signal });
        this.entranceFallback = window.setTimeout(reveal, 1800);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.entranceTimer);
      clearTimeout(this.entranceFallback);
      this.abortController?.abort();
      this.mediaObserver?.disconnect();
      this.motionContext?.revert();
      this.motionMatchMedia?.revert();
    }

    returnToGalleryStart() {
      if (!this.mediaList) return;
      const behavior = this.reducedMotion ? 'instant' : 'smooth';
      // Horizontal gallery resets for every variant; it must not move the page if already at its top.
      if (Math.abs(this.mediaList.scrollLeft) > 2 || this.mediaList.scrollTop > 2) {
        this.mediaList.scrollTo({ left: 0, top: 0, behavior });
      }
      // Use the stable gallery container, not the first image (which may change on finish selection).
      const galleryTop = this.mediaList.getBoundingClientRect().top;
      const headerOffset = 100;
      if (galleryTop < headerOffset - 90) {
        const top = Math.max(0, window.scrollY + galleryTop - headerOffset);
        window.scrollTo({ top, behavior });
      }
    }

    filterFinish(finishValue) {
      const finish = String(finishValue || '').trim().toLowerCase();
      const options = ['gold', 'silver', 'copper'];
      const hasTaggedImages = this.mediaItems.some(item => options.includes(item.dataset.finish));
      if (!hasTaggedImages || !options.includes(finish)) return;
      this.currentFinish = finish;
      const pairMedia = this.mediaItems.find(item => item.dataset.finish === 'pair' && item.dataset.pairFinish === finish)
        || this.mediaItems.find(item => item.dataset.finish === 'pair');
      const isPair = String(this.currentSelection || '').toLowerCase() === 'pair';
      const finishFrames = this.mediaItems.filter(item => item.dataset.finish === finish);
      const numberOf = item => {
        const img = item.querySelector('img.gsap-featured-product__image');
        const source = (img?.getAttribute('src') || '') + ' ' + (img?.alt || '');
        const match = source.match(/(?:gold|silver|copper)[-_ ](\d{2})(?:[_\. ]|$)/i);
        return match ? Number(match[1]) : 99;
      };
      const front = finishFrames.find(item => numberOf(item) === 1) || finishFrames[0];
      const detail = finishFrames.find(item => numberOf(item) === 4 && item !== front) ||
        finishFrames.find(item => item !== front);
      // Exactly two gallery frames for each selection; never expose legacy
      // cover photos or duplicate files imported for the Pair variant.
      const allowed = new Set(isPair && pairMedia
        ? [pairMedia, detail || front].filter(Boolean)
        : [front, detail].filter(Boolean));
      const visible = this.mediaItems.filter(item => {
        const show = allowed.has(item);
        item.hidden = !show;
        if (show) { const image = item.querySelector('img'); if (image) { image.loading = 'eager'; image.fetchPriority = 'high'; } }
        return show;
      });
      this.thumbnails.forEach(item => {
        const mediaId = item.dataset.mediaId;
        item.hidden = !visible.some(frame => frame.dataset.mediaId === mediaId);
      });
      this.syncGalleryCues();
      if (this.pagination) {
        this.pagination.replaceChildren();
        visible.forEach((frame, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gsap-featured-product__pagination-dot';
          dot.dataset.gfpMediaId = frame.dataset.mediaId;
          dot.setAttribute('aria-label', `${this.pagination.getAttribute('aria-label')} ${index + 1}`);
          dot.setAttribute('aria-current', String(index === 0));
          dot.addEventListener('click', () => this.selectMedia(frame.dataset.mediaId, true), { signal: this.abortController.signal });
          this.pagination.appendChild(dot);
        });
        this.pagination.hidden = visible.length < 2;
      }
      const first = visible[0];
      if (first) {
        this.selectMedia(first.dataset.mediaId, false);
        if (this.mediaList && matchMedia('(max-width: 989px)').matches) {
          this.mediaList.scrollTo({ left: 0, behavior: 'instant' });
        }
      }
      this.mediaObserver?.disconnect();
      if (this.mediaObserver) visible.forEach(item => this.mediaObserver.observe(item));
      window.ScrollTrigger?.refresh();
    }

    renderPairPreview(selection) {
      this.querySelectorAll('[data-gfp-pair-preview]').forEach(node => node.remove());
      this.mediaItems.forEach(frame => frame.classList.remove('is-pair-preview'));
      const isPair = String(selection || '').toLowerCase() === 'pair';
      const dedicatedPair = this.mediaItems.find(frame => frame.dataset.finish === 'pair' && frame.dataset.pairFinish === this.currentFinish)
        || this.mediaItems.find(frame => frame.dataset.finish === 'pair');
      if (dedicatedPair && isPair) {
        dedicatedPair.hidden = false;
        const pairImage = dedicatedPair.querySelector('img.gsap-featured-product__image');
        if (pairImage) pairImage.loading = 'eager';
        this.selectMedia(dedicatedPair.dataset.mediaId, false);
        return;
      }
      if (dedicatedPair) dedicatedPair.hidden = true;
      const visible = this.mediaItems.filter(frame => !frame.hidden && frame.querySelector('img.gsap-featured-product__image'));
      const first = visible.find(frame => /(?:gold|silver|copper)[-_ ]01(?:[_ .]|$)/i.test((frame.querySelector('img')?.src || '') + ' ' + (frame.querySelector('img')?.alt || ''))) || visible[0];
      if (!first) return;
      const original = first.querySelector('img.gsap-featured-product__image');
      if (!original) return;
      const pair = document.createElement('div');
      pair.className = 'gsap-featured-product__pair-preview';
      pair.dataset.gfpPairPreview = '';
      pair.setAttribute('aria-label', isPair ? 'Pair of two matching bracelets' : 'Single bracelet');
      for (let i = 0; i < (isPair ? 2 : 1); i++) {
        const image = document.createElement('img');
        image.width = original.naturalWidth || original.width || 2000;
        image.height = original.naturalHeight || original.height || 2000;
        image.src = original.src;
        if (original.srcset) image.srcset = original.srcset;
        if (original.sizes) image.sizes = original.sizes;
        image.className = 'gsap-featured-product__pair-image';
        image.alt = isPair ? (i === 0 ? 'First bracelet of pair' : 'Second bracelet of pair') : original.alt;
        image.loading = 'eager';
        image.decoding = 'async';
        image.classList.add('is-pair-image-loading');
        const showImage = async () => {
          if (!image.naturalWidth) { image.classList.add('has-image-error'); return; }
          try { await image.decode(); } catch (_) {}
          image.classList.remove('is-pair-image-loading');
        };
        if (image.complete) showImage();
        else {
          image.addEventListener('load', showImage, { once: true });
          image.addEventListener('error', () => image.classList.add('has-image-error'), { once: true });
        }
        pair.appendChild(image);
      }
      first.appendChild(pair);
      first.classList.add('is-pair-preview');
    }

    syncGalleryCues() {
      if (!this.galleryCues) return;
      const visible = this.mediaItems.filter(frame => !frame.hidden);
      const related = this.querySelector('[data-gfp-related]');
      this.mediaItems.forEach(frame => {
        const cue = this.galleryCues.get(frame);
        if (!cue) return;
        const index = visible.indexOf(frame);
        const hasNext = index >= 0 && index < visible.length - 1;
        cue.hidden = index < 0 || (!hasNext && !related);
        cue.setAttribute('aria-label', hasNext ? 'Scroll to next photo' : 'Scroll to You may also like');
      });
    }

    selectMedia(mediaId, shouldScroll = false) {
      const target = this.mediaItems.find(item => String(item.dataset.mediaId) === String(mediaId));
      if (!target) return;

      this.thumbnails.forEach(thumbnail => {
        const active = String(thumbnail.dataset.mediaId) === String(mediaId);
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', String(active));
      });

      this.pagination?.querySelectorAll('button').forEach(dot => {
        dot.setAttribute('aria-current', String(dot.dataset.gfpMediaId === String(mediaId)));
      });
      if (!shouldScroll) return;
      const behavior = this.reducedMotion ? 'auto' : 'smooth';
      if (matchMedia('(max-width: 989px)').matches && this.mediaList) {
        const left = this.mediaList.scrollLeft + target.getBoundingClientRect().left - this.mediaList.getBoundingClientRect().left;
        this.mediaList.scrollTo({ left, behavior });
        return;
      }

      window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - 48, behavior });
    }

    observeMedia() {
      if (!this.mediaItems.length || !('IntersectionObserver' in window)) return;
      const mobile = matchMedia('(max-width: 989px)').matches;
      this.mediaObserver = new IntersectionObserver(entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) this.selectMedia(visible.target.dataset.mediaId);
      }, {
        root: mobile ? this.mediaList : null,
        rootMargin: mobile ? '0px' : '-48% 0px -48% 0px',
        threshold: mobile ? [.35, .6, .85] : [0, .01]
      });
      this.mediaItems.forEach(item => this.mediaObserver.observe(item));
    }

    initMotion() {
      if (this.reducedMotion || !window.gsap || !window.ScrollTrigger) return;

      const gsap = window.gsap;
      gsap.registerPlugin(window.ScrollTrigger);
      const revealItems = [...this.querySelectorAll('[data-gfp-reveal]')];
      const motionDistance = Number.parseFloat(getComputedStyle(this).getPropertyValue('--gfp-motion-distance')) || 32;
      const parallaxStrength = Number.parseFloat(getComputedStyle(this).getPropertyValue('--gfp-parallax')) || 0;

      this.motionContext = gsap.context(() => {
        gsap.fromTo(revealItems, {
          opacity: 0,
          y: motionDistance
        }, {
          opacity: 1,
          y: 0,
          duration: .9,
          stagger: .065,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: this.querySelector('[data-gfp-content]') || this,
            start: 'top 82%',
            toggleActions: 'play none none reverse'
          }
        });

        this.motionMatchMedia = gsap.matchMedia();
        this.motionMatchMedia.add('(min-width: 990px)', () => {
          this.mediaItems.forEach(item => {
            gsap.fromTo(item, {clipPath: 'inset(3.5% 3.5% 3.5% 3.5%)'}, {
              clipPath: 'inset(0% 0% 0% 0%)', ease: 'none',
              scrollTrigger: {trigger: item, start: 'top 92%', end: 'top 35%', scrub: .7}
            });
          });
        });
        this.motionMatchMedia.add('(max-width: 989px)', () => {
          gsap.fromTo(this.mediaItems, {opacity: .65, scale: .985}, {
            opacity: 1, scale: 1, duration: .8, stagger: .08, ease: 'power3.out'
          });
        });
      }, this);

      window.ScrollTrigger.refresh();
    }
  });
}
